import React from 'react'
import { checkProviders, decodeJwtEmail, isApiError, signInWithGoogle, tokenStore } from '../api'
import { completeLogin } from '../utils/complete-login'
import { GOOGLE_CLIENT_ID, loadGoogleIdentity } from '../utils/google-gis'
import { SSO_LOGIN_DISABLED_MESSAGE } from '../utils/login-method-messages'
import { Button } from './Button'
import { useToast } from './Toast'
import type { GoogleCredentialResponse } from '../utils/google-gis'

// GIS requires a numeric width and does not support "100%", so we measure the
// container and pass its pixel width, re-rendering on resize. GIS caps the
// button at 400px; below that it tracks the container. GIS_BUTTON_WIDTH is the
// fallback used before the container has been measured.
const GIS_BUTTON_WIDTH = 336
const GIS_MAX_WIDTH = 400

export function GoogleLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_google_logo)">
        <path
          d="M19.7618 10.1871C19.7618 9.36773 19.6954 8.7698 19.5515 8.14972H10.1948V11.848H15.687C15.5763 12.7671 14.9783 14.1512 13.6496 15.0813L13.6309 15.2051L16.5893 17.497L16.7943 17.5174C18.6767 15.779 19.7618 13.2211 19.7618 10.1871Z"
          fill="#4285F4"
        />
        <path
          d="M10.1947 19.9313C12.8854 19.9313 15.1442 19.0454 16.7941 17.5174L13.6494 15.0813C12.8079 15.6682 11.6784 16.0779 10.1947 16.0779C7.55932 16.0779 5.3226 14.3395 4.52527 11.9366L4.4084 11.9466L1.33222 14.3273L1.29199 14.4391C2.93077 17.6945 6.29695 19.9313 10.1947 19.9313Z"
          fill="#34A853"
        />
        <path
          d="M4.52526 11.9366C4.31488 11.3166 4.19313 10.6521 4.19313 9.96565C4.19313 9.27908 4.31488 8.61473 4.51419 7.99466L4.50862 7.8626L1.39389 5.44366L1.29198 5.49214C0.616561 6.84305 0.229004 8.36008 0.229004 9.96565C0.229004 11.5712 0.616561 13.0882 1.29198 14.4391L4.52526 11.9366Z"
          fill="#FBBC05"
        />
        <path
          d="M10.1947 3.85336C12.066 3.85336 13.3283 4.66168 14.048 5.33718L16.8605 2.59107C15.1332 0.985496 12.8854 0 10.1947 0C6.29695 0 2.93077 2.23672 1.29199 5.49214L4.51421 7.99466C5.3226 5.59183 7.55932 3.85336 10.1947 3.85336Z"
          fill="#EB4335"
        />
      </g>
      <defs>
        <clipPath id="clip0_google_logo">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

/**
 * "Continue with Google" — drives the GIS sign-in flow.
 *
 * Google verification is itself a complete login (the identity provider owns
 * MFA, so AMLiQ never adds its own TOTP). Before signing in, it reads the email
 * from the Google ID token (`response.credential`) and checks the account-wide
 * login methods with `POST /auth/check-providers` — if the tenant admin has
 * disabled SSO the sign-in is blocked, without ever hitting `/auth/google`.
 * Otherwise it POSTs the ID token to `/auth/google` and stores the returned
 * session with a success toast. Sign-in failures and the disabled-method block
 * are reported via `onError` so the parent can surface them.
 *
 * When `VITE_GOOGLE_CLIENT_ID` is unset the button renders disabled.
 */
export function GoogleSignInButton({
  onError,
}: {
  /** Called with the failure message, or `null` to clear it. */
  onError: (message: string | null) => void
}) {
  const { showToast } = useToast()
  const buttonRef = React.useRef<HTMLDivElement | null>(null)
  const [pending, setPending] = React.useState(false)

  const handleCredential = React.useCallback(
    async (response: GoogleCredentialResponse) => {
      setPending(true)
      onError(null)
      try {
        // Respect the account-wide login-method setting: read the email from
        // the Google ID token and block when the tenant admin has disabled SSO,
        // so we never hit /auth/google. Enforcement is UI-side — the sign-in
        // endpoint doesn't reject a disabled method.
        const email = decodeJwtEmail(response.credential)
        if (email) {
          const { loginMethods } = await checkProviders(email)
          if (!loginMethods.ssoLoginEnabled) {
            onError(SSO_LOGIN_DISABLED_MESSAGE)
            setPending(false)
            return
          }
        }

        // SSO verification is a complete login — store the session and finish.
        const result = await signInWithGoogle(response.credential)
        if (result.token) {
          tokenStore.setAccessToken(result.token)
          showToast('Login success', 'success')
          void completeLogin()
        }
        setPending(false)
      } catch (err) {
        onError(isApiError(err) ? err.message : 'Google sign-in failed. Please try again.')
        setPending(false)
      }
    },
    [onError, showToast],
  )

  React.useEffect(() => {
    const container = buttonRef.current
    if (!GOOGLE_CLIENT_ID || !container) return

    let cancelled = false
    let ready = false
    let lastWidth = 0

    // Re-render the GIS button at the container's current width. GIS appends a
    // fresh iframe on each call, so clear the previous render first, and skip
    // when the (clamped, rounded) width is unchanged to avoid resize thrash.
    const renderButton = () => {
      if (cancelled || !ready || !window.google) return
      const width = Math.min(Math.round(container.offsetWidth) || GIS_BUTTON_WIDTH, GIS_MAX_WIDTH)
      if (width === lastWidth) return
      lastWidth = width
      container.replaceChildren()
      window.google.accounts.id.renderButton(container, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'center',
        width,
      })
    }

    loadGoogleIdentity()
      .then(() => {
        if (cancelled || !window.google) return
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredential,
        })
        ready = true
        renderButton()
      })
      .catch(() => {
        if (!cancelled) onError("Couldn't load Google Sign-In.")
      })

    // Track the container width so the button matches the card responsively.
    const observer = new ResizeObserver(() => renderButton())
    observer.observe(container)

    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [handleCredential, onError])

  // Not configured — show the styled button, disabled.
  if (!GOOGLE_CLIENT_ID) {
    return (
      <div className="flex flex-col gap-1.5">
        <Button type="button" id="btn-sso-google" variant="outline" size="lg" disabled>
          <GoogleLogo />
          Continue with Google
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div ref={buttonRef} className="gis-button flex justify-center" />

      {pending && <p className="text-center text-xs text-brand-muted">Signing in…</p>}
    </div>
  )
}
